import React, { createContext, useContext, ReactNode, useState, useEffect } from "react";
import {
  DEFAULT_PRODUCT_KEY,
  getProductDefinition,
  PRODUCT_REGISTRY,
  ProductKey,
} from "../config/products";
import { MaterialContent, QuestionnaireContent } from "../types/content";

interface AppContextProps {
  material: MaterialContent;
  setMaterial: React.Dispatch<React.SetStateAction<MaterialContent>>;
  questionnaire_material: QuestionnaireContent;
  setQuestionnaireMaterial: React.Dispatch<React.SetStateAction<QuestionnaireContent>>;
  selectedProduct: ProductKey | "";
  setSelectedProduct: React.Dispatch<React.SetStateAction<ProductKey | "">>;
  isEnglish: boolean;
  setIsEnglish: React.Dispatch<React.SetStateAction<boolean>>;
}

const AppContext = createContext<AppContextProps | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const defaultProduct = PRODUCT_REGISTRY[DEFAULT_PRODUCT_KEY];
  const [material, setMaterial] = useState<MaterialContent>(
    defaultProduct.material as MaterialContent
  );
  const [questionnaire_material, setQuestionnaireMaterial] = useState<QuestionnaireContent>(
    defaultProduct.questions as QuestionnaireContent
  );
  const [selectedProduct, setSelectedProduct] = useState<ProductKey | "">("");

  const [isEnglish, setIsEnglish] = useState<boolean>(false);

  // Update material based on selectedProduct
  useEffect(() => {
    const productDefinition = getProductDefinition(selectedProduct);

    if (!productDefinition) {
      setIsEnglish(false);
      return;
    }

    setMaterial(productDefinition.material as MaterialContent);
    setQuestionnaireMaterial(productDefinition.questions as QuestionnaireContent);
    setIsEnglish(Boolean(productDefinition.isEnglish));
  }, [selectedProduct]);

  return (
    <AppContext.Provider
      value={{
        material,
        setMaterial,
        questionnaire_material,
        setQuestionnaireMaterial,
        selectedProduct,
        setSelectedProduct,
        isEnglish,
        setIsEnglish,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
